import React, { useState, useRef, useEffect } from "react";
import { useLocation, useHistory } from "react-router-dom";
import Navbar from "components/Navbars/AuthNavbar";
import Footer from "components/Footers/Footer";
import BASE_URL from "components/BaseUrl/apiConfig";
import { Plus, MoveLeft } from "lucide-react";
import Swal from "sweetalert2";

const AddExpenseType = () => {
  const history = useHistory();
  const userNameInputRef = useRef(null);
  const location = useLocation();

  const [expenseTypeData, setExpenseTypeData] = useState(null);

  useEffect(() => {
    // Check if user is not logged in (localStorage is empty)
    if (!localStorage.getItem("userData")) {
      // Redirect to the login page
      history.push("/login");
    }
  }, [history]);

  useEffect(() => {
    // Focus on the userName input when the component mounts
    userNameInputRef.current.focus();
  }, []);

  useEffect(() => {
    // Check if data is available in the location state
    if (location.state && location.state.expenseType) {
      const { expenseType } = location.state;
      setFormData({
        expensedescTypeID: expenseType.expensedescTypeID || "",
        expenseTypeName: expenseType.expenseTypeName || "",
        defaultText: expenseType.defaulttext || "",
        type: expenseType.expenseTypeId || "",
      });
    }
  }, [location.state]);

  const [formData, setFormData] = useState({
    expenseTypeName: "",
    defaultText: "",
    type: "",
  });

  const [errors, setErrors] = useState({});

  //Get Useid form localStorage
  const storedUserData = localStorage.getItem("userData");
  let userId = "";
  if (storedUserData) {
    const userData = JSON.parse(storedUserData);
    userId = userData.userId;
  }

  const isEdit = !!formData.expensedescTypeID;

  const handleAddExpenseType = async (e) => {
    e.preventDefault();
    // Clear previous errors
    setErrors({});
    // Validate form data
    const validationErrors = {};
    if (!formData.expenseTypeName) {
      validationErrors.expenseTypeName = "Expense Type is required.";
    }
    if (!formData.type) {
      validationErrors.type = "Select Type.";
    }
    if (Object.keys(validationErrors).length > 0) {
      // If there are validation errors, set the state and return
      setErrors(validationErrors);
      return;
    }
    try {
      const url =
        location.state && location.state.expenseType
          ? `${BASE_URL}/api/ExpenseDescriptiontype/UpdateExpenseDescritionType`
          : `${BASE_URL}/api/ExpenseDescriptiontype/InsertExpenseDescritionType`;
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          expensedescTypeID: formData.expensedescTypeID || null, // Include expensedescTypeID if present
          expenseTypeName: formData.expenseTypeName,
          defaulttext: formData.defaultText,
          expenseTypeId: formData.type,
          userId: userId,
        }),
      });
      if (response.ok) {
        Swal.fire(
          "Success!",
          isEdit ? "Expense type updated successfully!" : "Expense type added successfully!",
          "success"
        );
        history.push("/getExpensetype");
      } else {
        console.error("Error adding expense type");
        Swal.fire("Error", "Failed to process expense type. Please try again later.", "error");
      }
    } catch (error) {
      console.error("Error adding expense type:", error);
      Swal.fire("Error", "Failed to process expense type. Please try again later.", "error");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen px-4 py-6 bg-gray-50">
        <div className="max-w-2xl mx-auto">
          {/* HEADER */}
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Plus className="h-10 w-10 text-gray-800" />
              <div>
                <h1 className="text-xl font-bold text-gray-800">
                  {isEdit ? "Edit Expense Type" : "Add New Expense Type"}
                </h1>
                <p className="text-gray-600">Define a new category for your expenses</p>
              </div>
            </div>
            <button
              onClick={() => history.goBack()}
              className="flex text-lg items-center gap-3 px-3 py-2 bg-blue-50 hover:bg-gray-100 rounded-lg"
            >
              <MoveLeft /> Back
            </button>
          </div>

          {/* CARD */}
          <div
            className="rounded-lg mt-6 p-8 bg-white"
            style={{
              boxShadow:
                "rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 2px 6px 2px"
            }}
          >
            <form onSubmit={handleAddExpenseType}>
              {/* EXPENSE TYPE NAME */}
              <label className="block font-medium mb-2 text-gray-700">
                Expense Type Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="expenseTypeName"
                name="expenseTypeName"
                maxLength={30}
                ref={userNameInputRef}
                value={formData.expenseTypeName}
                onChange={handleChange}
                placeholder="Enter expense type name"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {/* Hidden input for expensedescTypeID */}
              <input
                type="hidden"
                id="expensedescTypeID"
                name="expensedescTypeID"
                value={formData.expensedescTypeID}
              />
              {errors.expenseTypeName && (
                <p className="text-red-500 text-sm mt-1">{errors.expenseTypeName}</p>
              )}

              {/* DEFAULT TEXT */}
              <label className="block font-medium mb-2 mt-6 text-gray-700">Default Text</label>
              <input
                type="text"
                id="defaultText"
                maxLength={70}
                name="defaultText"
                value={formData.defaultText}
                onChange={handleChange}
                placeholder="Enter default description text"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">{formData.defaultText.length}/70</p>

              {/* TYPE */}
              <label className="block font-medium mb-2 mt-6 text-gray-700">
                Type <span className="text-red-500">*</span>
              </label>
              <select
                id="type"
                name="type"
                value={formData.type}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select Type</option>
                <option value="1">Credited</option>
                <option value="2">Debited</option>
              </select>
              {errors.type && (
                <p className="text-red-500 text-sm mt-1">{errors.type}</p>
              )}

              {/* BUTTONS */}
              <div className="flex justify-end gap-4 mt-8">
                <button
                  type="button"
                  onClick={() => history.goBack()}
                  className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 flex items-center gap-2 bg-blue-500 text-white rounded-md shadow hover:bg-blue-600 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  {isEdit ? "Update Expense Type" : "Add Expense Type"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default AddExpenseType;